import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Modal, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { selectUserDropdowns } from '../../redux/slices/dropdownSlice';
import {
  setRecords,
  addRecord,
  updateRecord,
  deleteRecord,
  setLoading,
  setError,
  setFilter,
  selectProtectedTimeRecords,
  selectProtectedTimeLoading,
  selectProtectedTimeError,
  selectProtectedTimeFilters,
} from '../../redux/slices/protectedTimeSlice';
import { fetchData, postData, patchData, deleteData } from '../../redux/slices/apiCallSlice';
import { formatDateForInput, formatDateForTable } from '../../utils/dateUtils';

const ProtectedTime = () => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const userDropdowns = useSelector(selectUserDropdowns);
  const records = useSelector(selectProtectedTimeRecords);
  const loading = useSelector(selectProtectedTimeLoading);
  const error = useSelector(selectProtectedTimeError);
  const filters = useSelector(selectProtectedTimeFilters);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  useRealtimeDraft(showModal || showDeleteModal);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    userName: '',
    startDate: '',
    endDate: '',
    notes: '',
    dayOfWeek: [],
    hoursPerWeek: '',
    weekPercent: '',
  });

  // Form validation error
  const [validationError, setValidationError] = useState('');

  // Get mechanical engineers from user dropdowns
  const mechanicalEngineers = useMemo(() => {
    return userDropdowns?.mechOptions || [];
  }, [userDropdowns]);

  const fetchRecords = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await dispatch(
        fetchData({ url: apiUrls.engineeringProtectedTime })
      ).unwrap();
      dispatch(setRecords(response));
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch protected time records'));
    }
  }, [dispatch, apiUrls.engineeringProtectedTime]);

  // Fetch all protected time records on mount
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Filter records based on selected engineer
  const filteredRecords = useMemo(() => {
    if (filters.userName === 'All') {
      return records;
    }
    return records.filter(record => record.UserName === filters.userName);
  }, [records, filters.userName]);

  // Sort records by StartDate descending (most recent first)
  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      const dateA = a.StartDate ? new Date(a.StartDate) : new Date(0);
      const dateB = b.StartDate ? new Date(b.StartDate) : new Date(0);
      return dateB - dateA;
    });
  }, [filteredRecords]);

  const handleOpenCreateModal = () => {
    setIsEditMode(false);
    setSelectedRecord(null);
    
    // Default startDate to today using dateUtils
    const today = formatDateForInput(new Date());
    
    setFormData({
      userName: '',
      startDate: today,
      endDate: '',
      notes: '',
      dayOfWeek: [],
      hoursPerWeek: '',
      weekPercent: '',
    });
    setValidationError('');
    setShowModal(true);
  };

  const handleOpenEditModal = (record) => {
    setIsEditMode(true);
    setSelectedRecord(record);
    
    // Parse DayOfWeek - could be string or array
    let parsedDayOfWeek = [];
    if (record.DayOfWeek) {
      if (Array.isArray(record.DayOfWeek)) {
        parsedDayOfWeek = record.DayOfWeek;
      } else if (typeof record.DayOfWeek === 'string') {
        parsedDayOfWeek = record.DayOfWeek.split(',').map(day => day.trim());
      }
    }

    setFormData({
      userName: record.UserName || '',
      startDate: formatDateForInput(record.StartDate),
      endDate: formatDateForInput(record.EndDate),
      notes: record.Notes || '',
      dayOfWeek: parsedDayOfWeek,
      hoursPerWeek: record.HoursPerWeek || '',
      weekPercent: record.WeekPercent || '',
    });
    setValidationError('');
    setShowModal(true);
  };

  const handleOpenDeleteModal = (record) => {
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedRecord(null);
    setValidationError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDayOfWeekChange = (day) => {
    setFormData(prev => {
      const currentDays = prev.dayOfWeek || [];
      if (currentDays.includes(day)) {
        return {
          ...prev,
          dayOfWeek: currentDays.filter(d => d !== day),
        };
      } else {
        return {
          ...prev,
          dayOfWeek: [...currentDays, day],
        };
      }
    });
  };

  const validateForm = () => {
    // Check that at least one of the three fields has a value
    const hasDayOfWeek = formData.dayOfWeek && formData.dayOfWeek.length > 0;
    const hasHoursPerWeek = formData.hoursPerWeek && formData.hoursPerWeek !== '';
    const hasWeekPercent = formData.weekPercent && formData.weekPercent !== '';

    if (!hasDayOfWeek && !hasHoursPerWeek && !hasWeekPercent) {
      setValidationError('At least one of DayOfWeek, HoursPerWeek, or WeekPercent must have a value');
      return false;
    }

    if (!formData.userName) {
      setValidationError('Engineer name is required');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    dispatch(setLoading(true));

    try {
      // Prepare payload for API
      // POST requires camelCase
      // PATCH requires PascalCase
      
      // Default startDate to today if not provided for new records
      const defaultStartDate = !isEditMode && !formData.startDate 
        ? formatDateForInput(new Date())
        : formData.startDate;
      
      const payload = isEditMode ? {
        ID: selectedRecord.ID,
        UserName: formData.userName,
        StartDate: formData.startDate || null,
        EndDate: formData.endDate || null,
        Notes: formData.notes || null,
        DayOfWeek: formData.dayOfWeek.length > 0 ? formData.dayOfWeek.join(',') : null,
        HoursPerWeek: formData.hoursPerWeek || null,
        WeekPercent: formData.weekPercent || null,
      } : {
        userName: formData.userName,
        startDate: defaultStartDate || null,
        endDate: formData.endDate || null,
        notes: formData.notes || null,
        dayOfWeek: formData.dayOfWeek.length > 0 ? formData.dayOfWeek.join(',') : null,
        hoursPerWeek: formData.hoursPerWeek || null,
        weekPercent: formData.weekPercent || null,
      };

      if (isEditMode) {
        // PATCH request
        const response = await dispatch(
          patchData({
            url: `${apiUrls.engineeringProtectedTime}?id=${selectedRecord.ID}`,
            data: payload,
          })
        ).unwrap();
        
        dispatch(updateRecord(response));
      } else {
        // POST request
        const response = await dispatch(
          postData({
            url: apiUrls.engineeringProtectedTime,
            data: payload,
          })
        ).unwrap();
        
        dispatch(addRecord(response));
      }

      handleCloseModal();
      // Refresh the list to ensure we have the latest data
      await fetchRecords();
    } catch (err) {
      dispatch(setError(err.message || 'Failed to save protected time record'));
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;

    dispatch(setLoading(true));

    try {
      await dispatch(
        deleteData({
          url: `${apiUrls.engineeringProtectedTime}?id=${selectedRecord.ID}`,
          deleteId: { ID: selectedRecord.ID },
        })
      ).unwrap();

      dispatch(deleteRecord(selectedRecord.ID));
      handleCloseModal();
    } catch (err) {
      dispatch(setError(err.message || 'Failed to delete protected time record'));
    }
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Engineering Protected Time</h2>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Add Protected Time
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Engineer</Form.Label>
            <Form.Select
              value={filters.userName}
              onChange={(e) => dispatch(setFilter({ key: 'userName', value: e.target.value }))}
            >
              <option value="All">All Engineers</option>
              {mechanicalEngineers.map((engineer) => (
                <option key={engineer.displayName} value={engineer.displayName}>
                  {engineer.displayName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Records Table */}
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading protected time records...</p>
        </div>
      ) : sortedRecords.length === 0 ? (
        <Alert variant="info">
          No protected time records found. Click "Add Protected Time" to create a new record.
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Engineer</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Day(s) of Week</th>
              <th>Hours/Week</th>
              <th>Week %</th>
              <th>Notes</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRecords.map((record) => (
              <tr key={record.ID}>
                <td>{record.UserName}</td>
                <td>{formatDateForTable(record.StartDate) || 'N/A'}</td>
                <td>{formatDateForTable(record.EndDate) || 'N/A'}</td>
                <td>
                  {record.DayOfWeek ? (
                    typeof record.DayOfWeek === 'string' 
                      ? record.DayOfWeek.split(',').map((day, idx) => (
                          <Badge key={idx} bg="info" className="me-1">
                            {day.trim()}
                          </Badge>
                        ))
                      : Array.isArray(record.DayOfWeek)
                      ? record.DayOfWeek.map((day, idx) => (
                          <Badge key={idx} bg="info" className="me-1">
                            {day}
                          </Badge>
                        ))
                      : 'N/A'
                  ) : 'N/A'}
                </td>
                <td>{record.HoursPerWeek || 'N/A'}</td>
                <td>{record.WeekPercent ? `${record.WeekPercent}%` : 'N/A'}</td>
                <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.Notes || ''}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleOpenEditModal(record)}
                  >
                    <i className="bi bi-pencil"></i>
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleOpenDeleteModal(record)}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit' : 'Add'} Protected Time</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationError && (
            <Alert variant="danger">{validationError}</Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Engineer <span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="userName"
                value={formData.userName}
                onChange={handleFormChange}
                required
              >
                <option value="">Select Engineer</option>
                {mechanicalEngineers.map((engineer) => (
                  <option key={engineer.displayName} value={engineer.displayName}>
                    {engineer.displayName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleFormChange}
                  />
                  <Form.Text className="text-muted">
                    Optional for recurring patterns
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleFormChange}
                  />
                  <Form.Text className="text-muted">
                    Optional for recurring patterns
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Day(s) of Week</Form.Label>
              <div>
                {daysOfWeek.map((day) => (
                  <Form.Check
                    key={day}
                    inline
                    type="checkbox"
                    label={day}
                    checked={formData.dayOfWeek.includes(day)}
                    onChange={() => handleDayOfWeekChange(day)}
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Select specific days for recurring protection
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hours Per Week</Form.Label>
                  <Form.Control
                    type="number"
                    name="hoursPerWeek"
                    value={formData.hoursPerWeek}
                    onChange={handleFormChange}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                  <Form.Text className="text-muted">
                    Total hours protected per week
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Week Percent</Form.Label>
                  <Form.Control
                    type="number"
                    name="weekPercent"
                    value={formData.weekPercent}
                    onChange={handleFormChange}
                    min="0"
                    max="100"
                    step="1"
                    placeholder="e.g., 25"
                  />
                  <Form.Text className="text-muted">
                    Percentage of week protected (0-100)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Optional notes about this protected time"
              />
            </Form.Group>

            <Alert variant="info">
              <small>
                <strong>Note:</strong> At least one of Day(s) of Week, Hours Per Week, or Week Percent must have a value.
                These fields can coexist (e.g., 8 hours split between Monday and Thursday).
              </small>
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditMode ? 'Save Changes' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this protected time record?</p>
          {selectedRecord && (
            <div>
              <strong>Engineer:</strong> {selectedRecord.UserName}<br />
              <strong>Period:</strong> {formatDateForTable(selectedRecord.StartDate) || 'N/A'} - {formatDateForTable(selectedRecord.EndDate) || 'N/A'}<br />
              {selectedRecord.Notes && (
                <>
                  <strong>Notes:</strong> {selectedRecord.Notes}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProtectedTime;
